import { apiRequest, apiRequestBlob, apiUpload } from '@/lib/api/client';
export type WorkflowModule =
  | 'profile'
  | 'topic'
  | 'plan'
  | 'nird'
  | 'practices'
  | 'internship'
  | 'publications'
  | 'achievements'
  | 'defense'
  | 'completion';
export type JsonRecord = Record<string, unknown>;
export interface WorkflowField {
  path: string;
  kind: string;
  type: string;
  input_type?: string;
  required: boolean;
  applies: boolean;
  done: boolean;
  label: { ru: string; kz: string; en: string };
  sub?: string;
  key?: string;
  root?: string;
  review?: { approved: boolean; stale: boolean; comment: string | null };
}
export interface DefenseGroup {
  id: string;
  title: WorkflowField['label'];
  desc: WorkflowField['label'];
  items: string[];
}
export interface WorkflowState {
  module: WorkflowModule;
  data: unknown;
  profile: JsonRecord;
  revision: string;
  fields: WorkflowField[];
  approved?: number;
  filled: number;
  required: number;
  percent: number | null;
  review_status: string;
  advisor_comment: string | null;
  comment_target?: string | null;
  last_save_kind?: 'manual' | 'autosave' | 'submit';
  answers_filled?: number;
  answers_required?: number;
  documents_filled?: number;
  documents_required?: number;
  instruction?: WorkflowInstruction;
  templates?: Resource[];
  submissions?: { id: string; revision: string; created_at: string }[];
  snapshot_id?: string;
  snapshot_created_at?: string;
  confirmation_cleared: boolean;
  options: Record<string, [string, string][]>;
  resources: Resource[];
  guidance?: DefenseGroup[];
  evidence_types?: [string, string][];
  history: {
    id: string;
    actor_name?: string;
    content_fingerprint?: string;
    decision: string;
    comment: string | null;
    target?: string;
    created_at: string;
  }[];
}
export interface WorkflowInstruction {
  title: WorkflowField['label'];
  intro: WorkflowField['label'];
  notes: WorkflowField['label'][];
  steps: [string, WorkflowField['label'], WorkflowField['label']][];
  templates_intro?: WorkflowField['label'];
}
export interface Resource {
  id: string;
  fileId?: string | null;
  group: string;
  title: string;
  filename?: string;
  description?: WorkflowField['label'];
  available?: boolean;
  local_available?: boolean;
  number?: number;
  field_path?: string;
  revision?: string;
  edition_id?: string | null;
}
export interface WorkflowOverview {
  modules: Pick<
    WorkflowState,
    'module' | 'filled' | 'required' | 'percent' | 'review_status' | 'revision'
  >[];
  percent: number | null;
  profile: JsonRecord;
  resources: Resource[];
  defense_groups?: DefenseGroup[];
  prime: { id: string; t: WorkflowField['label']; p: WorkflowField['label'] }[];
}
const base = '/doctoral/workflow';
const request = <T>(url: string, options: Parameters<typeof apiRequest>[1] = {}) =>
  apiRequest<T>(url, { ...options, channel: 'platform' });
export const workflowApi = {
  overview: (studentId?: string) =>
    request<WorkflowOverview>(base, { query: { student_id: studentId } }),
  load: (module: WorkflowModule, studentId?: string) =>
    request<WorkflowState>(`${base}/${module}`, { query: { student_id: studentId } }),
  save: (
    module: WorkflowModule,
    revision: string,
    values: JsonRecord,
    saveKind: 'manual' | 'autosave' = 'autosave',
  ) =>
    request<WorkflowState>(`${base}/${module}`, {
      method: 'PUT',
      body: { revision, values, save_kind: saveKind },
    }),
  record: (
    module: WorkflowModule,
    revision: string,
    record: JsonRecord,
    action = 'save',
    saveKind: 'manual' | 'autosave' = 'manual',
  ) =>
    request<WorkflowState>(`${base}/${module}/records`, {
      method: 'POST',
      body: { revision, record, action, save_kind: saveKind },
    }),
  upload: (
    module: WorkflowModule,
    revision: string,
    path: string,
    file: File,
    evidence?: Record<string, string>,
  ) =>
    apiUpload<WorkflowState>(`${base}/${module}/files`, file, 'file', {
      path,
      revision,
      ...evidence,
    }),
  detach: (module: WorkflowModule, revision: string, path: string, fileId: string) =>
    request<WorkflowState>(`${base}/${module}/detach`, {
      method: 'POST',
      body: { revision, path, file_id: fileId },
    }),
  submit: (module: WorkflowModule, revision: string) =>
    request<WorkflowState>(`${base}/${module}/submit`, { method: 'POST', body: { revision } }),
  decide: (
    module: WorkflowModule,
    revision: string,
    studentId: string,
    decision: string,
    comment: string,
    target?: string,
  ) =>
    request<WorkflowState>(`${base}/${module}/decision`, {
      method: 'POST',
      body: { revision, student_id: studentId, decision, comment, target },
    }),
  fieldDecision: (
    module: WorkflowModule,
    revision: string,
    studentId: string,
    path: string,
    approved: boolean,
    comment: string,
  ) =>
    request<WorkflowState>(`${base}/${module}/field-decision`, {
      method: 'POST',
      body: { revision, student_id: studentId, path, approved, comment },
    }),
  document: (
    fileId: string,
    versionId: string,
    studentId?: string,
    disposition = 'attachment',
    submissionId?: string,
  ) =>
    apiRequestBlob(`${base}/files/${encodeURIComponent(fileId)}/${encodeURIComponent(versionId)}`, {
      channel: 'platform',
      query: { student_id: studentId, disposition, submission_id: submissionId },
    }),
  preview: (fileId: string, versionId: string, studentId?: string, submissionId?: string) =>
    request<DocumentPreview>(
      `${base}/files/${encodeURIComponent(fileId)}/${encodeURIComponent(versionId)}/preview`,
      { query: { student_id: studentId, submission_id: submissionId } },
    ),
  template: (id: string, editionId?: string | null) =>
    apiRequestBlob(
      editionId
        ? `${base}/resources/${encodeURIComponent(editionId)}/file`
        : `${base}/templates/${encodeURIComponent(id)}`,
      {
        channel: 'platform',
        query: { disposition: 'attachment' },
      },
    ),
  templatePreview: (id: string, editionId?: string | null) =>
    request<DocumentPreview>(
      editionId
        ? `${base}/resources/${encodeURIComponent(editionId)}/preview`
        : `${base}/templates/${encodeURIComponent(id)}/preview`,
    ),
  submission: (module: WorkflowModule, id: string, studentId?: string) =>
    request<WorkflowState>(`${base}/${module}/submissions/${encodeURIComponent(id)}`, {
      query: { student_id: studentId },
    }),
};
export interface DocumentPreview {
  kind: 'docx' | 'unsupported';
  blocks?: { kind: 'paragraph' | 'table'; text?: string; rows?: string[][] }[];
}
export function pathValue(data: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (value, key) => (value && typeof value === 'object' ? (value as JsonRecord)[key] : undefined),
      data,
    );
}

export const workflowTitles: Record<WorkflowModule, string> = {
  profile: 'Мой профиль',
  topic: 'Тема и консультанты',
  plan: 'Индивидуальный план',
  nird: 'НИРД',
  practices: 'Педагогическая и исследовательская практика',
  internship: 'Научная стажировка',
  publications: 'Публикации',
  achievements: 'Научные и творческие достижения',
  defense: 'Предзащита и защита',
  completion: 'Завершение обучения',
};
